import React, { useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FiExternalLink, FiSearch } from "react-icons/fi";
import Particle from "../Particle";
import useInViewOnce from "../../hooks/useInViewOnce";
import VideoGallery from "./VideoGallery";
import {
  LAB_OWNER,
  featuredLabProject,
  labProjects,
  ownerLabels,
} from "./labProjects";

const ProjectCard = ({ project }) => {
  return (
    <div className="policy-card lab-project-card">
      {project.image ? (
        <div className="lab-project-image" aria-hidden="true">
          <img src={project.image} alt="" loading="lazy" />
        </div>
      ) : (
        <div
          className="lab-project-image lab-project-image--placeholder"
          aria-hidden="true"
        />
      )}

      <div className="lab-project-body">
        <div className="lab-project-top">
          <h3 className="lab-project-title">{project.title}</h3>
          <span className="lab-project-owner">{ownerLabels[project.owner]}</span>
        </div>

        {project.description ? (
          <p className="lab-project-desc">{project.description}</p>
        ) : null}

        {Array.isArray(project.tags) && project.tags.length > 0 ? (
          <div className="lab-project-tags" aria-label="Project tags">
            {project.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="lab-tag">
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {project.href ? (
          <div className="lab-project-cta">
            <a
              className="btn custom-yellow-btn lab-cta-btn"
              href={project.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {project.cta ?? "View"} <FiExternalLink className="lab-cta-icon" />
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const LabPage = () => {
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [query, setQuery] = useState("");

  const { ref: heroRef, hasEntered: heroSeen } = useInViewOnce({
    threshold: 0.16,
    once: true,
  });
  const { ref: featuredRef, hasEntered: featuredSeen } = useInViewOnce({
    threshold: 0.16,
    once: true,
  });
  const { ref: projectsRef, hasEntered: projectsSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: modelingRef, hasEntered: modelingSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });

  const normalizedQuery = query.trim().toLowerCase();

  const filteredProjects = useMemo(() => {
    return labProjects.filter((project) => {
      if (ownerFilter !== "all" && project.owner !== ownerFilter) return false;

      if (!normalizedQuery) return true;
      const haystack = `${project.title ?? ""} ${project.description ?? ""} ${(project.tags ?? []).join(" ")}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [ownerFilter, normalizedQuery]);

  const choicestProjects = filteredProjects.filter(
    (project) => project.owner === LAB_OWNER.choicest,
  );
  const internProjects = filteredProjects.filter(
    (project) => project.owner === LAB_OWNER.interns,
  );

  return (
    <Container fluid className="about-section lab-page">
      <Particle />

      <Container className="lab-container">
        <header
          ref={heroRef}
          className={`lab-hero lab-reveal${heroSeen ? " is-seen" : ""}`}
        >
          <h1 className="lab-title">
            Project <strong className="purple">Lab</strong>
          </h1>
          <p className="lab-lead">
            Explore real work from the Choicest Fruit team and our interns—web apps,
            3D web experiences, and creative experiments.
          </p>

          <div className="policy-card lab-controls" aria-label="Project filters">
            <div className="lab-controls-row">
              <div className="lab-search">
                <FiSearch aria-hidden="true" className="lab-search-icon" />
                <input
                  className="lab-search-input"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search projects (e.g., Favrhood, 3D, portfolio)"
                  aria-label="Search projects"
                />
              </div>

              <div
                className="lab-pills"
                role="tablist"
                aria-label="Filter by creator"
              >
                <button
                  type="button"
                  className={`lab-pill${ownerFilter === "all" ? " is-active" : ""}`}
                  onClick={() => setOwnerFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`lab-pill${ownerFilter === LAB_OWNER.choicest ? " is-active" : ""}`}
                  onClick={() => setOwnerFilter(LAB_OWNER.choicest)}
                >
                  Choicest Fruit
                </button>
                <button
                  type="button"
                  className={`lab-pill${ownerFilter === LAB_OWNER.interns ? " is-active" : ""}`}
                  onClick={() => setOwnerFilter(LAB_OWNER.interns)}
                >
                  Interns
                </button>
              </div>
            </div>

            <div className="lab-jump" aria-label="Jump to section">
              <a className="lab-jump-link" href="#featured">
                Featured
              </a>
              <a className="lab-jump-link" href="#choicest">
                Choicest Fruit
              </a>
              <a className="lab-jump-link" href="#interns">
                Intern Projects
              </a>
              <a className="lab-jump-link" href="#modeling">
                3D Modeling
              </a>
            </div>
          </div>
        </header>

        <section
          id="featured"
          ref={featuredRef}
          className={`lab-section lab-reveal${featuredSeen ? " is-seen" : ""}`}
          aria-label="Featured project"
        >
          <h2 className="about-section-heading">Featured</h2>

          <div className="policy-card lab-featured">
            <Row className="align-items-center g-4">
              <Col md={4}>
                <div className="lab-featured-media" aria-hidden="true">
                  <img src={featuredLabProject.image} alt="" loading="lazy" />
                </div>
              </Col>
              <Col md={8}>
                <h3 className="lab-featured-title">{featuredLabProject.title}</h3>
                <p className="lab-featured-desc">{featuredLabProject.description}</p>

                <div className="lab-project-tags" aria-label="Featured project tags">
                  {(featuredLabProject.tags ?? []).map((tag) => (
                    <span key={tag} className="lab-tag">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="lab-featured-actions">
                  <a
                    className="btn custom-yellow-btn"
                    href={featuredLabProject.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {featuredLabProject.cta ?? "Visit"}{" "}
                    <FiExternalLink className="lab-cta-icon" />
                  </a>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <section
          ref={projectsRef}
          className={`lab-section lab-reveal${projectsSeen ? " is-seen" : ""}`}
          aria-label="Project lists"
        >
          <Row className="g-4">
            <Col lg={12}>
              <div id="choicest" className="lab-subsection">
                <h2 className="about-section-heading">Choicest Fruit Projects</h2>
                <p className="lab-sublead">
                  Products and platforms created by the Choicest Fruit team.
                </p>

                {choicestProjects.length === 0 ? (
                  <div className="policy-card lab-empty">
                    No matching Choicest Fruit projects.
                  </div>
                ) : (
                  <div className="lab-grid" role="list">
                    {choicestProjects.map((project) => (
                      <div key={project.id} role="listitem">
                        <ProjectCard project={project} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div id="interns" className="lab-subsection">
                <h2 className="about-section-heading">Intern Projects</h2>
                <p className="lab-sublead">
                  Hands-on builds, portfolio work, and creative experiments by our
                  interns.
                </p>

                {internProjects.length === 0 ? (
                  <div className="policy-card lab-empty">No matching intern projects.</div>
                ) : (
                  <div className="lab-grid" role="list">
                    {internProjects.map((project) => (
                      <div key={project.id} role="listitem">
                        <ProjectCard project={project} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </section>

        <section
          id="modeling"
          ref={modelingRef}
          className={`lab-section lab-reveal${modelingSeen ? " is-seen" : ""}`}
          aria-label="3D modeling and animation"
        >
          <h2 className="about-section-heading">3D Modeling & Animation</h2>
          <p className="lab-sublead">
            A gallery of modeling, simulation, and animation explorations.
          </p>

          <div className="policy-card lab-modeling-card">
            <VideoGallery />
          </div>
        </section>
      </Container>
    </Container>
  );
};

export default LabPage;
